import { useState, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { createTechnician, deleteTechnician } from '../lib/api'
import { DEPT_COLORS, DEPT_PRIORITY, darkenHex } from '../lib/deptColors'
import { useAuth } from '../hooks/useAuth'

const DEPT_LABELS = {
  MX: 'Maintenance', AVI: 'Avionics', INT: 'Interior',
  PA: 'Paint', QA: 'Quality Assurance', EXT: 'External',
}

function TechFormModal({ tech, orgDepts, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name:  tech?.first_name  || '',
    last_name:   tech?.last_name   || '',
    department:  tech?.department  || (orgDepts[0]?.key ?? 'MX'),
  })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState(null)

  const handleSave = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First and last name are required.')
      return
    }
    setSaving(true)
    setError(null)
    if (tech?.id) {
      await supabase.from('technicians').update({
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim(),
        department: form.department,
      }).eq('id', tech.id)
    } else {
      await createTechnician({
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim(),
        department: form.department,
      })
    }
    setSaving(false)
    onSaved()
    onClose()
  }

  const handleKey = e => { if (e.key === 'Enter') handleSave() }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">
          {tech?.id ? 'Edit Technician' : 'Add Technician'}
        </h3>

        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">First Name</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                value={form.first_name}
                onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                onKeyDown={handleKey}
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Last Name</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                value={form.last_name}
                onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                onKeyDown={handleKey}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Department</label>
            <select
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
              value={form.department}
              onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
            >
              {orgDepts.map(d => (
                <option key={d.key} value={d.key}>{d.label || DEPT_LABELS[d.key] || d.key}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-brand-red hover:bg-brand-red-dark text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function TechnicianRegistry({ technicians, refetch, isEditor }) {
  const { profile } = useAuth()
  const orgDepts = profile?.orgs?.settings?.departments ?? []
  const orgDeptMap = useMemo(() => Object.fromEntries(orgDepts.map(d => [d.key, d])), [orgDepts])
  const orgDeptOrder = useMemo(() => orgDepts.map(d => d.key), [orgDepts])

  const deptLabel = (key) => orgDeptMap[key]?.label || DEPT_LABELS[key] || key
  const deptHex   = (key) => orgDeptMap[key]?.hex || '#9ca3af'

  const [search, setSearch]               = useState('')
  const [expandedDepts, setExpandedDepts] = useState(() => new Set(orgDepts.map(d => d.key)))
  const [showModal, setShowModal]         = useState(false)
  const [editTech, setEditTech]           = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)

  const filtered = useMemo(() => {
    if (!search) return technicians
    const q = search.toLowerCase()
    return technicians.filter(t =>
      t.first_name.toLowerCase().includes(q) ||
      t.last_name.toLowerCase().includes(q)  ||
      deptLabel(t.department).toLowerCase().includes(q)
    )
  }, [technicians, search, orgDeptMap])

  const byDept = useMemo(() => {
    const map = {}
    filtered.forEach(t => {
      const d = t.department || '—'
      if (!map[d]) map[d] = []
      map[d].push(t)
    })
    Object.values(map).forEach(arr =>
      arr.sort((a, b) => a.last_name.localeCompare(b.last_name))
    )
    return map
  }, [filtered])

  const activeDepts = useMemo(() => [
    ...orgDeptOrder.filter(d => byDept[d]),
    ...Object.keys(byDept).filter(d => !orgDeptOrder.includes(d)),
  ], [byDept, orgDeptOrder])

  const toggleDept = d => setExpandedDepts(prev => {
    const n = new Set(prev); n.has(d) ? n.delete(d) : n.add(d); return n
  })

  const openAdd  = () => { setEditTech(null); setShowModal(true) }
  const openEdit = t  => { setEditTech(t);    setShowModal(true) }
  const closeModal = () => { setShowModal(false); setEditTech(null) }

  const handleDelete = async id => {
    await deleteTechnician(id)
    refetch()
    setConfirmDelete(null)
  }

  return (
    <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">Technician Roster</h2>
        {isEditor && (
          <button
            onClick={openAdd}
            className="flex items-center gap-1.5 bg-brand-red hover:bg-brand-red-dark text-white text-sm font-medium px-3 py-1.5 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Technician
          </button>
        )}
      </div>

      {/* Search */}
      <div className="px-6 py-3 border-b border-gray-100">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search by name or department…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
          />
        </div>
      </div>

      {/* Department groups */}
      <div className="overflow-y-auto px-4 py-2">
        {activeDepts.length === 0 && (
          <div className="text-center text-gray-400 text-sm py-12">
            {search
              ? 'No technicians match your search'
              : 'No technicians yet — click Add Technician to get started'}
          </div>
        )}

        {activeDepts.map(dept => {
          const hex   = deptHex(dept)
          const techs = byDept[dept] || []
          const open  = expandedDepts.has(dept)

          return (
            <div key={dept} className="mb-1">
              {/* Dept header row */}
              <button
                onClick={() => toggleDept(dept)}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
              >
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: hex }} />
                <span className="font-semibold text-gray-800 text-sm flex-1">{deptLabel(dept)}</span>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{techs.length}</span>
              </button>

              {/* Technician rows */}
              {open && (
                <div className="ml-6 space-y-0.5 pb-1">
                  {techs.map(t => (
                    <div
                      key={t.id}
                      className="group flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {/* Avatar */}
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: hex + '33', color: darkenHex(hex) }}
                      >
                        {t.first_name[0]}{t.last_name[0]}
                      </div>

                      <span className="text-sm text-gray-800 flex-1">
                        {t.first_name} {t.last_name}
                      </span>

                      {/* Actions */}
                      {isEditor && (
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          {confirmDelete === t.id ? (
                            <>
                              <span className="text-xs text-gray-500 mr-1">Delete?</span>
                              <button
                                onClick={() => handleDelete(t.id)}
                                className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded font-medium transition-colors"
                              >
                                Yes
                              </button>
                              <button
                                onClick={() => setConfirmDelete(null)}
                                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded font-medium transition-colors"
                              >
                                No
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => openEdit(t)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                                title="Edit"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => setConfirmDelete(t.id)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                title="Delete"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {showModal && (
        <TechFormModal
          tech={editTech}
          orgDepts={orgDepts}
          onClose={closeModal}
          onSaved={refetch}
        />
      )}
    </div>
  )
}
